import React, { useMemo } from "react";
import { connect } from "react-redux";

import Highcharts from "highcharts";
import { defineMessages, useIntl } from "react-intl";

import useChangeTracking from "@mapmycustomers/shared/util/hook/useChangeTracking";

import sampleDataImg from "@app/assets/dashboard/sampleWrapper.svg";
import CardType from "@app/enum/dashboard/CardType";
import PreviewType from "@app/enum/dashboard/PreviewType";
import OrganizationSetting from "@app/enum/OrganizationSetting";
import {
  GRID_MARGIN,
  GRID_ROW_HEIGHT,
} from "@app/scene/dashboard/components/DashboardContent/util/consts";
import getCardByType from "@app/scene/dashboard/utils/getCardByType";
import {
  getOrganizationSettingValue,
  isCurrentUserOwner,
  isMileageTrackingVisible,
} from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import CardConfiguration from "@app/types/dashboard/CardConfiguration";
import Scope from "@app/types/dashboard/Scope";

import CustomerFaceTimeAlert from "./CustomerFaceTimeAlert";
import Section from "./EditCardForm/components/Section";
import MileageTrackingUpsellModal from "./MileageTrackingUpsellModal";
import styles from "./Preview.module.scss";

const messages = defineMessages({
  description: {
    id: "dashboard.addEditCard.preview.description",
    defaultMessage: "Actual size and layout may vary based on dashboard arrangement",
    description: "Preview section description in Add/Edit Card modal on the Dashboard page",
  },
  placeholder: {
    id: "dashboard.addEditCard.preview.placeholder",
    defaultMessage: "Select a chart to see preview",
    description: "Placeholder in preview section in Add/Edit Card modal on the Dashboard page",
  },
  title: {
    id: "dashboard.addEditCard.preview.title",
    defaultMessage: "Preview",
    description: "Preview section title in Add/Edit Card modal on the Dashboard page",
  },
});

interface Props {
  areActiveCheckInsEnabled: boolean;
  configuration?: CardConfiguration<any, any, any>;
  isMileageTrackingEnabled: boolean;
  isOwner: boolean;
  previewType?: PreviewType;
  scope: Scope;
  type?: CardType;
}

const Preview: React.FC<Props> = ({
  areActiveCheckInsEnabled,
  configuration,
  isMileageTrackingEnabled,
  isOwner,
  previewType = PreviewType.REAL_DATA,
  scope,
  type,
}) => {
  const intl = useIntl();
  const CardUi = useMemo(() => (type ? getCardByType(type)! : undefined), [type]);

  const cardDimensions = CardUi?.getCardDimensions({ configuration, scope, viewState: {} });
  const previewHeight = cardDimensions
    ? GRID_ROW_HEIGHT * cardDimensions.h + GRID_MARGIN[1] * (cardDimensions.h - 1)
    : 0;

  useChangeTracking(() => {
    // charts don't handle height changes very well, so we're force-reflowing them to fit better
    // when card height changes. Wish we could only reflow the ones we need...
    setTimeout(() => {
      Highcharts.charts.forEach((chart) => chart?.reflow());
    }, 3000);
  }, [previewHeight]);

  return (
    <Section className={styles.container} title={intl.formatMessage(messages.title)}>
      <div className={styles.description}>{intl.formatMessage(messages.description)}</div>
      {!areActiveCheckInsEnabled && type === CardType.CUSTOMER_FACE_TIME && (
        <CustomerFaceTimeAlert isOwner={isOwner} />
      )}
      {!isMileageTrackingEnabled && type === CardType.MILEAGE_TRACKING && (
        <MileageTrackingUpsellModal isOwner={isOwner} />
      )}
      <div className={styles.preview} style={{ height: previewHeight ? previewHeight : undefined }}>
        {CardUi && configuration ? (
          <CardUi.CardContentComponent
            configuration={configuration}
            id="preview"
            preview={previewType}
            scope={scope}
          />
        ) : (
          <div className={styles.placeholder}>{intl.formatMessage(messages.placeholder)}</div>
        )}
        {previewType === PreviewType.SAMPLE_DATA && (
          <div className={styles.sampleWrapper}>
            <img alt="Sample data" src={sampleDataImg} />
          </div>
        )}
      </div>
    </Section>
  );
};

const mapStateToProps = (state: RootState) => ({
  areActiveCheckInsEnabled: getOrganizationSettingValue(state)(OrganizationSetting.ACTIVE_CHECK_IN),
  isMileageTrackingEnabled: isMileageTrackingVisible(state),
  isOwner: isCurrentUserOwner(state),
});

export default connect(mapStateToProps)(Preview);
